import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

/**
 * DÉCLENCHEUR DU PANNEAU « NUMÉRO INCONNU » (LOT-NUMÉRO-INCONNU).
 *
 * Le numéro d'une ligne SANS fiche contact — journal des appels, liste « À
 * rappeler », enregistrements — rendu en bouton : cible tactile de 44 px
 * (`min-h-touch`), numéro insécable (jamais coupé chiffre par chiffre), le
 * brut en `title`, et un nom accessible qui dit ce qui va s'ouvrir.
 *
 * Chaque surface garde SON affichage du numéro (`children`) : brut dans les
 * journaux, groupé par paires dans « À rappeler » — le bouton ne reformate
 * rien. `phone` est le brut, celui que le panneau cherchera.
 */
export interface UnknownNumberButtonProps {
  /** Numéro brut (E.164 de préférence) — passé au panneau. */
  phone: string
  onOpen: (phone: string) => void
  /** Texte affiché ; défaut : le brut. */
  children?: ReactNode
  /** Numéro tel qu'il est LU dans le nom accessible ; défaut : `children` s'il est une chaîne, sinon le brut. */
  label?: string
  /**
   * #396 — nom de la fiche quand le numéro en a une (liste « À rappeler ») :
   * la surface affiche alors le NOM (`children`), et le NUMÉRO passe en
   * infobulle (forme lisible `label`) et dans le nom accessible, après le nom.
   */
  name?: string | null
  className?: string
}

export function UnknownNumberButton({
  phone,
  onOpen,
  children,
  label,
  name,
  className = '',
}: UnknownNumberButtonProps) {
  const { t } = useTranslation('contacts')
  const spoken = label ?? (typeof children === 'string' ? children : phone)
  return (
    <button
      type="button"
      title={name ? spoken : phone}
      aria-label={
        name
          ? t('contacts:unknownNumber.openNamed', { name, number: spoken })
          : t('contacts:unknownNumber.open', { number: spoken })
      }
      onClick={() => onOpen(phone)}
      className={[
        '-mx-1 inline-flex min-h-touch max-w-full items-center rounded-control px-1 text-left',
        'whitespace-nowrap tabular-nums underline decoration-border underline-offset-4',
        'hover:text-foreground hover:decoration-foreground-secondary',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
        className,
      ]
        .join(' ')
        .trim()}
    >
      {children ?? phone}
    </button>
  )
}

export default UnknownNumberButton
